import { IonIcon, IonToggle } from '@ionic/react';
import { FieldArray, useField, useFormikContext } from 'formik';
import { checkmarkCircle, closeCircle } from 'ionicons/icons';
import { ReactElement, useMemo } from 'react';
import { NumericInput } from '../../../../../../../../components/inputs/numeric-input';
import i18n from '../../../../../../../../i18n';
import { FlowItemArrayType } from '../../../../../../shared/model/flow.types';
import { BeaconWeldingData } from '../../../../model/beacon-welding-data';
import { MultipassGuidedViewModal } from '../guided-view/multipass-guided-view-modal';
import { MultipassSchedulePostActionInput } from '../multipass-schedule-post-action-input';
import { MultipassScheduleWeldLibraryInput } from '../multipass-schedule-weld-library-input';
import { MultipassScheduleTableViewProps } from './multipass-schedule-table.types';
import './multipass-schedule-table-edit-view.css';

const MultipassScheduleTableEditView = (
  props: MultipassScheduleTableViewProps
): ReactElement => {
  const { rootPass } = props;

  return (
    <div className="multipass-schedule-edit">
      <table>
        <thead>
          <tr>
            <th></th>
            <th>
              {i18n.t('welding:multipass_schedule_header_start_offset_abbr')}
            </th>
            <th>
              {i18n.t('welding:multipass_schedule_header_end_offset_abbr')}
            </th>
            <th>
              {i18n.t(
                'welding:multipass_schedule_header_perpendicular_to_path_abbr'
              )}
            </th>
            <th>
              {i18n.t('welding:multipass_schedule_header_torch_offset_abbr')}
            </th>
            <th>
              {i18n.t('welding:multipass_schedule_header_travel_angle_abbr')}
            </th>
            <th>
              {i18n.t('welding:multipass_schedule_header_work_angle_abbr')}
            </th>
            <th>
              {i18n.t('welding:multipass_schedule_header_reverse_path_abbr')}
            </th>
            <th>{i18n.t('welding:weld_library')}</th>
            <th>{i18n.t('common:post_action')}</th>
            {/* Empty header used for teach column */}
            <th></th>
          </tr>
          <MultipassScheduleRow
            label={i18n.t('welding:multipass_root_pass')}
            passIndex={-1}
            rootPass={rootPass}
            fieldNamePrefix="rootPassOffsets"
            readonly={true}
            isRootPass={true}
            disabled={props.disabled}
            presentingEl={props.presentingEl}
            numberOfPasses={props.numberOfPasses}
          />
        </thead>
        <tbody>
          <FieldArray
            name="schedule"
            render={() => <MultipassScheduleRows {...props} />}
          />
        </tbody>
      </table>
    </div>
  );
};

interface MultipassScheduleRowsProps extends MultipassScheduleTableViewProps {
  rootPass: FlowItemArrayType[];
}

const MultipassScheduleRows = (
  props: MultipassScheduleRowsProps
): ReactElement => {
  const { numberOfPasses, disabled, presentingEl, rootPass } = props;

  const rows = useMemo(() => {
    const rows = [];

    for (let i = 0; i < numberOfPasses; i++) {
      const fieldNamePrefix = `schedule[${i}]`;
      const passNumber = i + 2;
      const passLabel = i18n.t('common:pass_with_number', {
        number: passNumber,
      });
      rows.push(
        <MultipassScheduleRow
          key={i}
          fieldNamePrefix={fieldNamePrefix}
          label={passLabel}
          passIndex={i}
          disabled={disabled}
          presentingEl={presentingEl}
          rootPass={rootPass}
          numberOfPasses={numberOfPasses}
        />
      );
    }

    return rows;
  }, [disabled, numberOfPasses, presentingEl, rootPass]);

  return <>{rows}</>;
};

interface MultipassScheduleRowProps {
  fieldNamePrefix: string;
  passIndex: number;
  rootPass: FlowItemArrayType[];
  label: string;
  readonly?: boolean;
  disabled?: boolean;
  presentingEl?: HTMLElement;
  numberOfPasses: number;
  isRootPass?: boolean;
}

const MultipassScheduleRow = (
  props: MultipassScheduleRowProps
): ReactElement => {
  const {
    fieldNamePrefix,
    label,
    disabled,
    readonly,
    presentingEl,
    passIndex,
    rootPass,
    numberOfPasses,
    isRootPass,
  } = props;

  const { errors } =
    useFormikContext<BeaconWeldingData.MultipassSettingsFormData>();

  const hasError = useMemo(() => {
    return errors?.schedule && Array.isArray(errors.schedule)
      ? errors.schedule[passIndex]
      : undefined;
  }, [errors, passIndex]);

  return (
    <tr>
      <td className="label-cell">
        <div>
          {!readonly && (
            <IonIcon
              icon={hasError ? closeCircle : checkmarkCircle}
              color={hasError ? 'danger' : 'success'}
              className="status-icon"
            />
          )}
        </div>
        {label}
      </td>
      <td>
        <MultipassScheduleNumericInput
          name={`${fieldNamePrefix}.startOffsetX`}
          disabled={disabled}
          readonly={readonly}
        />
      </td>
      <td>
        <MultipassScheduleNumericInput
          name={`${fieldNamePrefix}.endOffsetX`}
          disabled={disabled}
          readonly={readonly}
        />
      </td>
      <td>
        <MultipassScheduleNumericInput
          name={`${fieldNamePrefix}.perpendicularToPathY`}
          disabled={disabled}
          readonly={readonly}
        />
      </td>
      <td>
        <MultipassScheduleNumericInput
          name={`${fieldNamePrefix}.stickoutZ`}
          disabled={disabled}
          readonly={readonly}
        />
      </td>
      <td>
        <MultipassScheduleNumericInput
          name={`${fieldNamePrefix}.travelAngle`}
          disabled={disabled}
          readonly={readonly}
        />
      </td>
      <td>
        <MultipassScheduleNumericInput
          name={`${fieldNamePrefix}.workAngle`}
          disabled={disabled}
          readonly={readonly}
        />
      </td>
      <td>
        {!readonly && (
          <MultipassScheduleToggleInput
            name={`${fieldNamePrefix}.reversePath`}
            disabled={disabled}
          />
        )}
      </td>
      <td>
        {!readonly && (
          <MultipassScheduleWeldLibraryInput
            name={`${fieldNamePrefix}.weldLibrary`}
            disabled={disabled}
            presentingEl={presentingEl}
            label={label}
            isFullScreen={true}
          />
        )}
      </td>
      <td>
        <MultipassSchedulePostActionInput
          name={
            isRootPass
              ? 'rootPassPostActions'
              : `${fieldNamePrefix}.postActions`
          }
          disabled={disabled}
          presentingEl={presentingEl}
          isFullScreen={true}
        />
      </td>
      <td>
        {!readonly && (
          <MultipassGuidedViewModal
            disabled={disabled}
            presentingEl={presentingEl}
            isFullScreen={true}
            passIndex={passIndex}
            rootPass={rootPass}
            numberOfPasses={numberOfPasses}
          />
        )}
      </td>
    </tr>
  );
};

const MultipassScheduleNumericInput = (props: {
  name: string;
  disabled?: boolean;
  readonly?: boolean;
}): ReactElement => {
  const { name, disabled, readonly } = props;
  const [field, _meta, helper] = useField(name);

  return (
    <NumericInput
      requireNegative={true}
      ionInputProps={{
        onIonChange: (e) => {
          const val = e.detail.value;
          helper.setValue(val === '' ? null : Number(val));
        },
        value: field.value,
        disabled,
        readonly,
      }}
    />
  );
};

const MultipassScheduleToggleInput = (props: {
  name: string;
  disabled?: boolean;
}): ReactElement => {
  const { name, disabled } = props;
  const [field, _meta, helper] = useField(name);

  return (
    <IonToggle
      onIonChange={(e) => {
        helper.setValue(e.detail.checked);
      }}
      checked={field.value}
      disabled={disabled}
    />
  );
};

export { MultipassScheduleTableEditView };
